import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { passwordProblem } from "@devengo/shared";

import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  Notice,
  PageTitle,
  tableClass,
  tdClass,
  thClass,
} from "../components/ui";
import { api } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useCurrentUser } from "../lib/session";

interface OpenSession {
  id: string;
  createdAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  current: boolean;
}
interface Device {
  id: string;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  ipAddress: string | null;
  userAgent: string | null;
}

/** «Mi cuenta»: change the password, see where the account is open (plan de seguridad). */
export function AccountPage() {
  return (
    <>
      <PageTitle subtitle="Tu clave, dónde tenés la sesión abierta y qué dispositivos recordás.">
        Mi cuenta
      </PageTitle>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PasswordCard />
        <SessionsCard />
      </div>
    </>
  );
}

function PasswordCard() {
  const me = useCurrentUser();
  const client = useQueryClient();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const change = useMutation({
    mutationFn: () =>
      api.post("/api/auth/password", { currentPassword: current, newPassword: next }),
    onSuccess: async () => {
      setCurrent("");
      setNext("");
      setRepeat("");
      await client.invalidateQueries({ queryKey: ["my-sessions"] });
    },
  });
  const problem = next ? passwordProblem(next, { email: me.email, name: me.name }) : null;
  return (
    <Card title="Cambiar mi clave">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          change.mutate();
        }}
      >
        <Field label="Clave actual">
          <Input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
        <Field
          label="Clave nueva"
          error={problem ?? undefined}
          hint="Al menos 10 caracteres. Una frase que recuerdes sirve."
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>
        <Field
          label="Repetila"
          error={repeat && repeat !== next ? "No coincide con la anterior." : undefined}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
          />
        </Field>
        <ErrorBox error={change.error} />
        {change.isSuccess ? (
          <Notice tone="success">
            Listo. Se cerraron tus otras sesiones, los dispositivos recordados y los asistentes
            conectados.
          </Notice>
        ) : null}
        <div>
          <Button
            type="submit"
            disabled={!current || !next || problem !== null || repeat !== next || change.isPending}
          >
            Cambiar clave
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** «Chrome en macOS» from a user agent: enough to recognise one's own devices. */
function deviceName(userAgent: string | null): string {
  if (!userAgent) return "Dispositivo desconocido";
  const browser = /Edg\//.test(userAgent)
    ? "Edge"
    : /Chrome\//.test(userAgent)
      ? "Chrome"
      : /Firefox\//.test(userAgent)
        ? "Firefox"
        : /Safari\//.test(userAgent)
          ? "Safari"
          : "Navegador";
  const os = /Windows/.test(userAgent)
    ? "Windows"
    : /iPhone|iPad/.test(userAgent)
      ? "iPhone/iPad"
      : /Android/.test(userAgent)
        ? "Android"
        : /Mac OS X/.test(userAgent)
          ? "macOS"
          : /Linux/.test(userAgent)
            ? "Linux"
            : "otro sistema";
  return `${browser} en ${os}`;
}

function SessionsCard() {
  const client = useQueryClient();
  const data = useQuery({
    queryKey: ["my-sessions"],
    queryFn: () => api.get<{ sessions: OpenSession[]; devices: Device[] }>("/api/auth/sessions"),
  });
  const refresh = () => client.invalidateQueries({ queryKey: ["my-sessions"] });
  const close = useMutation({
    mutationFn: (id: string) => api.delete(`/api/auth/sessions/${id}`),
    onSuccess: refresh,
  });
  const closeOthers = useMutation({
    mutationFn: () => api.post("/api/auth/sessions/close-others"),
    onSuccess: refresh,
  });
  const forget = useMutation({
    mutationFn: (id: string) => api.delete(`/api/auth/devices/${id}`),
    onSuccess: refresh,
  });
  const others = data.data?.sessions.filter((s) => !s.current).length ?? 0;
  return (
    <Card
      title="Mis sesiones"
      actions={
        others > 0 ? (
          <Button
            variant="secondary"
            disabled={closeOthers.isPending}
            onClick={() => closeOthers.mutate()}
          >
            Cerrar todas las demás
          </Button>
        ) : null
      }
    >
      <ErrorBox error={data.error ?? close.error ?? closeOthers.error ?? forget.error} />
      <table className={tableClass}>
        <thead>
          <tr>
            <th className={thClass}>Dispositivo</th>
            <th className={thClass}>Desde</th>
            <th className={thClass} />
          </tr>
        </thead>
        <tbody>
          {data.data?.sessions.map((s) => (
            <tr key={s.id}>
              <td className={tdClass}>
                {deviceName(s.userAgent)}{" "}
                {s.current ? <Badge tone="success">Esta sesión</Badge> : null}
                <div className="text-xs text-brand-muted">{s.ipAddress ?? ""}</div>
              </td>
              <td className={tdClass}>{formatDateTime(s.createdAt)}</td>
              <td className={`${tdClass} text-right`}>
                {s.current ? null : (
                  <Button variant="ghost" onClick={() => close.mutate(s.id)}>
                    Cerrar
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <h3 className="mt-5 text-sm font-semibold">Dispositivos recordados</h3>
      <p className="text-xs text-brand-muted">
        En estos no se pide el código por mail hasta la fecha indicada. Cambiar la clave los olvida
        a todos.
      </p>
      {data.data && data.data.devices.length === 0 ? (
        <p className="mt-2 text-sm text-brand-muted">No tenés dispositivos recordados.</p>
      ) : (
        <table className={`${tableClass} mt-2`}>
          <thead>
            <tr>
              <th className={thClass}>Dispositivo</th>
              <th className={thClass}>Último uso</th>
              <th className={thClass}>Hasta</th>
              <th className={thClass} />
            </tr>
          </thead>
          <tbody>
            {data.data?.devices.map((d) => (
              <tr key={d.id}>
                <td className={tdClass}>
                  {deviceName(d.userAgent)}
                  <div className="text-xs text-brand-muted">{d.ipAddress ?? ""}</div>
                </td>
                <td className={tdClass}>{formatDateTime(d.lastUsedAt)}</td>
                <td className={tdClass}>{formatDateTime(d.expiresAt)}</td>
                <td className={`${tdClass} text-right`}>
                  <Button variant="ghost" onClick={() => forget.mutate(d.id)}>
                    Olvidar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
